import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Logo } from './Logo';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';
import { FUNZIONALITA } from '@/content/funzionalita';

const LINKS = [
  { label: 'Clienti', to: '/clienti' },
  { label: 'Chi siamo', to: '/chi-siamo' },
  { label: 'Manifesto', to: '/manifesto' },
  { label: 'Team', to: '/team' },
];

export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileFeaturesOpen, setMobileFeaturesOpen] = useState(false);
  const location = useLocation();
  const inFunzionalita =
    location.pathname === '/prodotto' || location.pathname.startsWith('/funzionalita/');

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 12);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  // Force scrolled style on non-home pages (or with the mobile menu open) so the nav has contrast.
  const opaque = scrolled || location.pathname !== '/' || mobileOpen;

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-colors duration-300',
        opaque
          ? 'border-b border-border bg-background/75 backdrop-blur-xl'
          : 'bg-transparent',
      )}
    >
      <div className="mx-auto flex h-16 w-full max-w-[1240px] items-center justify-between px-6 md:px-10">
        <Link to="/" className="text-foreground" aria-label="Vai alla home">
          <Logo size={26} />
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          <div
            className="relative"
            onMouseEnter={() => setMenuOpen(true)}
            onMouseLeave={() => setMenuOpen(false)}
          >
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-expanded={menuOpen}
              aria-haspopup="true"
              className={cn(
                'inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm transition-colors',
                inFunzionalita || menuOpen
                  ? 'text-foreground bg-secondary/60'
                  : 'text-fg-2 hover:text-foreground',
              )}
            >
              Funzionalità
              <ChevronDown size={14} className={cn('transition-transform', menuOpen && 'rotate-180')} />
            </button>

            <AnimatePresence>
              {menuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.18 }}
                  className="absolute left-1/2 top-full w-[620px] -translate-x-1/2 pt-3"
                >
                  <div className="overflow-hidden rounded-xl border border-input bg-card shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]">
                    <div className="grid grid-cols-2 gap-1 p-2">
                      {FUNZIONALITA.map((f) => (
                        <NavLink
                          key={f.slug}
                          to={`/funzionalita/${f.slug}`}
                          className={({ isActive }) =>
                            cn(
                              'group rounded-lg p-3 transition-colors hover:bg-secondary/70',
                              isActive && 'bg-secondary/60',
                            )
                          }
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-[10px] text-primary">{f.code}</span>
                            <span className="text-[14px] text-foreground">{f.name}</span>
                          </div>
                          <p className="mt-1 line-clamp-2 pl-6 text-[12px] leading-[1.45] text-fg-2">{f.teaser}</p>
                        </NavLink>
                      ))}
                    </div>
                    <Link
                      to="/prodotto"
                      className="flex items-center justify-between border-t border-border bg-secondary/40 px-5 py-3 text-[13px] text-fg-2 transition-colors hover:text-foreground"
                    >
                      Panoramica del prodotto
                      <span aria-hidden>→</span>
                    </Link>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                cn(
                  'rounded-md px-3 py-1.5 text-sm transition-colors',
                  isActive
                    ? 'text-foreground bg-secondary/60'
                    : 'text-fg-2 hover:text-foreground',
                )
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="https://staging.dbxelvhq5y9b9.amplifyapp.com/commesse/COM426/cockpit"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden px-3 py-1.5 text-sm text-fg-2 transition-colors hover:text-foreground md:inline-flex"
          >
            Accedi
          </a>
          <Button asChild size="sm" variant="invert">
            <Link to="/candidature">
              Candidati <span aria-hidden>→</span>
            </Link>
          </Button>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? 'Chiudi il menu' : 'Apri il menu'}
            aria-expanded={mobileOpen}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-foreground transition-colors hover:bg-secondary/60 md:hidden"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-border bg-background md:hidden"
          >
            <div className="flex max-h-[calc(100dvh-4rem)] flex-col gap-1 overflow-y-auto px-6 py-4">
              <button
                type="button"
                onClick={() => setMobileFeaturesOpen((v) => !v)}
                aria-expanded={mobileFeaturesOpen}
                className={cn(
                  'flex items-center justify-between rounded-md px-3 py-2.5 text-left text-[15px] transition-colors',
                  inFunzionalita ? 'text-foreground bg-secondary/60' : 'text-fg-2 hover:text-foreground',
                )}
              >
                Funzionalità
                <ChevronDown size={16} className={cn('transition-transform', mobileFeaturesOpen && 'rotate-180')} />
              </button>
              {mobileFeaturesOpen && (
                <div className="mb-1 ml-3 flex flex-col border-l border-border pl-2">
                  <NavLink to="/prodotto" className="rounded-md px-3 py-2 text-[14px] text-fg-2 hover:text-foreground">
                    Panoramica
                  </NavLink>
                  {FUNZIONALITA.map((f) => (
                    <NavLink
                      key={f.slug}
                      to={`/funzionalita/${f.slug}`}
                      className={({ isActive }) =>
                        cn(
                          'rounded-md px-3 py-2 text-[14px] transition-colors',
                          isActive ? 'text-foreground bg-secondary/60' : 'text-fg-2 hover:text-foreground',
                        )
                      }
                    >
                      {f.name}
                    </NavLink>
                  ))}
                </div>
              )}
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={({ isActive }) =>
                    cn(
                      'rounded-md px-3 py-2.5 text-[15px] transition-colors',
                      isActive
                        ? 'text-foreground bg-secondary/60'
                        : 'text-fg-2 hover:text-foreground',
                    )
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="my-2 h-px bg-border" />
              <a
                href="https://staging.dbxelvhq5y9b9.amplifyapp.com/commesse/COM426/cockpit"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md px-3 py-2.5 text-[15px] text-fg-2 transition-colors hover:text-foreground"
              >
                Accedi
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
